import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../components/api/axiosInstance';
import logger from '../utils/logger';

const AdminDataContext = createContext();

export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within AdminDataProvider');
  }
  return context;
};

// Cache duration in milliseconds
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const AdminDataProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState({});
  const cacheRef = useRef({});
  const loadingRef = useRef({});

  // Keep refs in sync with state
  cacheRef.current = cache;
  loadingRef.current = loading;

  // Check if data is still valid
  const isCacheValid = (cacheEntry) => {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
  };

  // Generic fetch function with caching — reads from refs to avoid dependency churn
  const fetchData = useCallback(async (key, apiCall, forceRefresh = false) => {
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && cacheRef.current[key] && isCacheValid(cacheRef.current[key])) {
      return cacheRef.current[key].data;
    }

    // Prevent multiple simultaneous requests for the same data
    if (loadingRef.current[key]) {
      return cacheRef.current[key]?.data || null;
    }

    try {
      setLoading(prev => ({ ...prev, [key]: true }));

      const response = await apiCall();
      const data = response.data;

      // Cache the response
      setCache(prev => ({
        ...prev,
        [key]: {
          data,
          timestamp: Date.now()
        }
      }));

      return data;
    } catch (error) {
      logger.error(`Error fetching ${key}:`, error);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Specific data fetchers
  const getUsers = useCallback((forceRefresh = false) => {
    return fetchData('users', () => axiosInstance.get('/api/business/users', {
      params: { page: 1, limit: 100 }
    }), forceRefresh);
  }, [fetchData]);

  const getDomains = useCallback((forceRefresh = false) => {
    return fetchData('domains', () => axiosInstance.get('/api/business/domains'), forceRefresh);
  }, [fetchData]);

  const getTeams = useCallback((forceRefresh = false) => {
    return fetchData('teams', () => axiosInstance.get('/api/business/teams'), forceRefresh);
  }, [fetchData]);

  const getStorage = useCallback((forceRefresh = false) => {
    return fetchData('storage', () => axiosInstance.get('/api/business/storage/analytics?timeRange=30d'), forceRefresh);
  }, [fetchData]);

  const getDashboard = useCallback((forceRefresh = false) => {
    return fetchData('dashboard', () => axiosInstance.get('/api/business/dashboard'), forceRefresh);
  }, [fetchData]);

  const getReports = useCallback((forceRefresh = false) => {
    return fetchData('reports', () => axiosInstance.get('/api/business/reports'), forceRefresh);
  }, [fetchData]);

  const getRoles = useCallback((forceRefresh = false) => {
    return fetchData('roles', () => axiosInstance.get('/api/business/roles'), forceRefresh);
  }, [fetchData]);

  const getActivity = useCallback((forceRefresh = false) => {
    return fetchData('activity', () => axiosInstance.get('/api/business/activity/recent'), forceRefresh);
  }, [fetchData]);

  // Clear specific cache entry
  const clearCache = useCallback((key) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[key];
      return newCache;
    });
  }, []);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    setCache({});
  }, []);

  // Update cache with new data (useful after creating/updating records)
  const updateCache = useCallback((key, newData) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data: newData,
        timestamp: Date.now()
      }
    }));
  }, []);

  // Get cached data without making API call
  const getCachedData = useCallback((key) => {
    const cacheEntry = cache[key];
    if (cacheEntry && isCacheValid(cacheEntry)) {
      return cacheEntry.data;
    }
    return null;
  }, [cache]);

  // Check if data is loading
  const isLoading = useCallback((key) => {
    return loading[key] || false;
  }, [loading]);

  const value = useMemo(() => ({
    getUsers,
    getDomains,
    getTeams,
    getStorage,
    getDashboard,
    getReports,
    getRoles,
    getActivity,
    clearCache,
    clearAllCache,
    updateCache,
    getCachedData,
    isLoading,
    cache,
    isCacheValid: (key) => cache[key] && isCacheValid(cache[key])
  }), [getUsers, getDomains, getTeams, getStorage, getDashboard, getReports, getRoles, getActivity, clearCache, clearAllCache, updateCache, getCachedData, isLoading, cache]);

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
};

export default AdminDataProvider;